/**
 * Onboarding's Meet Your Agents card (`show_suggested_agents`): what runs for
 * the firm by itself and the member's own "email me when I'm away". Owners
 * and admins get switches for the firm agents; everyone else sees On or Off.
 * The turn is paused on this card (a client-resolved interrupt, like
 * `connect_apps`): Save or Skip writes the outcome as the tool part's output
 * and the turn resumes. The server checks the role again before applying.
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import {
  Bell,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  Hourglass,
  Inbox,
  Lock,
  ScanSearch,
  Upload,
  Zap,
  type LucideIcon
} from "lucide-react";
import { useMemo, useRef, useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { StatusBadge } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import type {
  SuggestedAgent,
  SuggestedAgentsInput,
  SuggestedAgentsOutput
} from "../types";

type AnyToolPart = ToolUIPart | DynamicToolUIPart;

type Props = {
  part: AnyToolPart;
  onResolve?: (toolCallId: string, output: SuggestedAgentsOutput) => void;
  className?: string;
};

const ICONS: Record<string, { icon: LucideIcon; tone: string }> = {
  scan: { icon: ScanSearch, tone: "bg-info-soft text-info" },
  inbox: { icon: Inbox, tone: "bg-accent-soft text-accent" },
  upload: { icon: Upload, tone: "bg-purple-soft text-purple" },
  hourglass: { icon: Hourglass, tone: "bg-orange-soft text-orange" },
  building: { icon: Building2, tone: "bg-success-soft text-success" },
  calendar: { icon: CalendarClock, tone: "bg-warn-soft text-warn" },
  zap: { icon: Zap, tone: "" }
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** The card's rows, from the part's input as the server announced it. */
export const suggestedAgentsFromInput = (
  input: unknown
): SuggestedAgentsInput => {
  if (!isRecord(input))
    return { admin: false, owner_name: null, firm_agents: [], personal: [] };
  const firm: SuggestedAgent[] = Array.isArray(input.firm_agents)
    ? input.firm_agents.flatMap((a): SuggestedAgent[] =>
        isRecord(a) && str(a.id)
          ? [
              {
                id: str(a.id),
                kind: a.kind === "skill" ? "skill" : "event",
                name: str(a.name) || str(a.id),
                description: str(a.description),
                icon: str(a.icon) || "zap",
                on: Boolean(a.on),
                editable: Boolean(a.editable)
              }
            ]
          : []
      )
    : [];
  const personal = Array.isArray(input.personal)
    ? input.personal.flatMap((p) =>
        isRecord(p) && str(p.id)
          ? [
              {
                id: str(p.id),
                name: str(p.name) || str(p.id),
                description: str(p.description),
                on: Boolean(p.on)
              }
            ]
          : []
      )
    : [];
  return {
    admin: Boolean(input.admin),
    owner_name: str(input.owner_name) || null,
    firm_agents: firm,
    personal
  };
};

/** Only the switches the member moved. */
export const changedSwitches = (
  initial: Record<string, boolean>,
  current: Record<string, boolean>
): Record<string, boolean> =>
  Object.fromEntries(
    Object.entries(current).filter(([id, on]) => initial[id] !== on)
  );

const AgentIcon: FC<{ icon: string }> = ({ icon }) => {
  const entry = ICONS[icon] ?? ICONS.zap;
  return <IconTile icon={entry.icon} className={entry.tone} />;
};

const Row: FC<{
  icon: ReactNode;
  name: ReactNode;
  description: string;
  control: ReactNode;
}> = ({ icon, name, description, control }) => (
  <li className="flex items-center gap-3 px-2.5 py-2">
    {icon}
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5 font-medium">{name}</div>
      {description ? (
        <div className="text-sm text-fg-3">{description}</div>
      ) : null}
    </div>
    {control}
  </li>
);

export const SuggestedAgentsCard: FC<Props> = ({
  part,
  onResolve,
  className
}) => {
  const card = useMemo(
    () => suggestedAgentsFromInput(part.input),
    [part.input]
  );
  const initialFirm = useMemo(
    () => Object.fromEntries(card.firm_agents.map((a) => [a.id, a.on])),
    [card.firm_agents]
  );
  const initialPersonal = useMemo(
    () => Object.fromEntries(card.personal.map((p) => [p.id, p.on])),
    [card.personal]
  );
  const [firm, setFirm] = useState<Record<string, boolean>>(initialFirm);
  const [personal, setPersonal] =
    useState<Record<string, boolean>>(initialPersonal);
  const resolved = useRef(false);
  const [resolving, setResolving] = useState(false);

  const resolve = (out: SuggestedAgentsOutput) => {
    if (resolved.current || !onResolve) return;
    resolved.current = true;
    setResolving(true);
    onResolve(part.toolCallId, out);
  };

  if (part.state === "output-available") {
    const skipped = isRecord(part.output) && Boolean(part.output.skipped);
    const on = card.firm_agents.filter((a) => firm[a.id] ?? a.on).length;
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md bg-fill-1 px-3 py-2 text-base",
          className
        )}
        data-testid="suggested-agents-resolved"
      >
        {skipped ? (
          <Zap className="size-3.5 shrink-0 text-fg-3" />
        ) : (
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
        )}
        <span className="min-w-0 flex-1 truncate font-medium">
          {skipped
            ? "Skipped agents for now"
            : `${on} firm agent${on === 1 ? "" : "s"} on`}
        </span>
      </div>
    );
  }

  const busy = part.state === "input-streaming" || resolving;
  const { admin } = card;

  return (
    <div
      className={cn(
        "rounded-xl bg-elevated p-3 text-base inset-ring inset-ring-line-2",
        busy && "opacity-70",
        className
      )}
      data-testid="suggested-agents-card"
    >
      <div className="flex items-center gap-1.5 text-fg-3">
        <Zap className="size-3.5 shrink-0" />
        <span>Meet Your Agents</span>
      </div>
      <p className="mt-1 text-sm text-fg-3">
        {admin
          ? "Each one works in the client's channel and asks before it changes anything. Turn on the ones you want; you can change them in Settings later."
          : `${
              card.owner_name ?? "Your firm's owners"
            } chose which agents run for the firm. They work in your clients' channels and ask before changing anything.`}
      </p>
      {card.firm_agents.length ? (
        <>
          <div className="mt-2.5 flex items-center justify-between px-0.5 pb-1.5 text-sm text-fg-3">
            <span>Firm Agents</span>
            {admin ? null : (
              <span className="inline-flex items-center gap-1 text-fg-4">
                <Lock className="size-3" />
                Owners and admins change these
              </span>
            )}
          </div>
          <ul className="divide-y divide-line-1 rounded-md bg-fill-1">
            {card.firm_agents.map((a) => (
              <Row
                key={a.id}
                icon={<AgentIcon icon={a.icon} />}
                name={
                  <>
                    {a.name}
                    {a.kind === "skill" ? <Tag>Firm Automation</Tag> : null}
                  </>
                }
                description={a.description}
                control={
                  admin && a.editable ? (
                    <Switch
                      checked={firm[a.id] ?? a.on}
                      disabled={busy}
                      aria-label={a.name}
                      onCheckedChange={(v) =>
                        setFirm((cur) => ({ ...cur, [a.id]: v }))
                      }
                    />
                  ) : a.on ? (
                    <StatusBadge tone="success">
                      <Check />
                      On
                    </StatusBadge>
                  ) : (
                    <StatusBadge>Off</StatusBadge>
                  )
                }
              />
            ))}
          </ul>
        </>
      ) : null}
      {card.personal.length ? (
        <>
          <div className="mt-3 px-0.5 pb-1.5 text-sm text-fg-3">
            Just for You
          </div>
          <ul className="divide-y divide-line-1 rounded-md bg-fill-1">
            {card.personal.map((p) => (
              <Row
                key={p.id}
                icon={<IconTile icon={Bell} />}
                name={p.name}
                description={p.description}
                control={
                  <Switch
                    checked={personal[p.id] ?? p.on}
                    disabled={busy}
                    aria-label={p.name}
                    onCheckedChange={(v) =>
                      setPersonal((cur) => ({ ...cur, [p.id]: v }))
                    }
                  />
                }
              />
            ))}
          </ul>
        </>
      ) : null}
      {onResolve ? (
        <div className="mt-2 flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-fg-3"
            disabled={busy}
            onClick={() => resolve({ skipped: true })}
            data-testid="suggested-agents-skip"
          >
            Skip for Now
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={() =>
              resolve({
                switches: {
                  firm: admin ? changedSwitches(initialFirm, firm) : {},
                  personal: changedSwitches(initialPersonal, personal)
                }
              })
            }
            data-testid="suggested-agents-save"
          >
            Save
          </Button>
        </div>
      ) : null}
    </div>
  );
};
